import { tx as translateInterfaceText } from '@/utils/i18n';
import { toast } from 'sonner';

/**
 * Downloads a video file from a URL
 * @param videoUrl - The URL of the video to download
 * @param videoTitle - The title to use for the downloaded file
 */
export async function downloadVideo(videoUrl: string, videoTitle: string) {
  try {
    toast.loading(translateInterfaceText("Preparando download..."));
    
    // Fetch the video
    const response = await fetch(videoUrl);
    
    if (!response.ok) {
      throw new Error(translateInterfaceText("Erro ao buscar o vídeo"));
    }
    
    // Get the blob
    const blob = await response.blob();
    
    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = blobUrl;
    
    // Sanitize the filename and add extension
    const sanitizedTitle = videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const extension = getVideoExtension(blob.type) || 'mp4';
    link.download = `${sanitizedTitle}.${extension}`;
    
    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);
    
    toast.dismiss();
    toast.success(translateInterfaceText("Download iniciado com sucesso"));
  } catch (error) {
    console.error('Error downloading video:', error);
    toast.dismiss();
    toast.error(translateInterfaceText("Erro ao baixar o vídeo. Tente novamente."));
  }
}

/**
 * Gets the file extension based on MIME type
 */
function getVideoExtension(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/ogg': 'ogg',
    'video/quicktime': 'mov',
    'video/x-msvideo': 'avi',
    'video/x-matroska': 'mkv',
  };
  
  return mimeToExt[mimeType] || 'mp4';
}

/**
 * Alternative method using direct link (for cases where CORS prevents blob download)
 * @param videoUrl - The URL of the video
 * @param videoTitle - The title for the downloaded file
 */
export function downloadVideoDirectLink(videoUrl: string, videoTitle: string) {
  try {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.mp4`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success(translateInterfaceText("Download iniciado"));
  } catch (error) {
    console.error('Error downloading video:', error);
    toast.error(translateInterfaceText("Erro ao baixar o vídeo"));
  }
}
